/**
 * Replay a SED's own in-band amendment trail over a refreshed document.
 *
 * WHY THIS IS A WHOLE-SED POST-PASS AND NOT PART OF THE SECTION GRAFT.
 * `topUpSiteSed` replaces evidence in more places than the provider-section
 * graft loop: `merged.lcoe` / `merged.incentives` are whole-slot assignments,
 * `stampInterconnectionAndIncentives` / `stampMultiHazardAndResources` /
 * `stampFuelSupplyAndLcoe` rewrite sections outright, and
 * `applyDocAwareConfidence` runs LAST and can rewrite `slot.data` through
 * `DOC_OVERRIDE_RULES` (a utility service letter owns
 * `grid.capacity` / `grid.utilityName` / `grid.voltage`). An amendment
 * re-applied inside the graft loop was therefore still liable to be
 * overwritten a few hundred lines later, in the same call. Running once at the
 * end — the position `bootstrapSiteSedServer` already uses for the rebuild
 * path — is the only placement where "the operator's decision wins" is
 * actually true when the function returns.
 *
 * WHY NOT `planAmendmentReapply`. That planner solves the REBUILD case: a
 * freshly compiled SED with `amendments: []`, fed the Firestore `SEDAmendment`
 * records, and it APPENDS a mirror row to `next.amendments` for each reapply.
 * Here the document already carries its own trail, so appending would grow the
 * array without bound on every refresh. The replay SEMANTICS below are
 * deliberately identical to that planner's — including the three-way test and
 * the refusal to overwrite a provider that moved — so the two paths cannot
 * drift on what a user decision is worth.
 *
 * SEQUENTIAL REPLAY IS LOAD-BEARING. An earlier version of this logic reduced
 * each field to its LAST amendment row and compared that row's
 * `originalValue` against the provider. That is wrong, and it silently
 * reintroduced the very bug this machinery exists to stop. The SED variable
 * browser records `oldValue: v.value` — the value ON SCREEN — so the second
 * correction of a field stores `{original: <first correction>, corrected:
 * <second correction>}`, NOT the provider baseline. Replaying only the last
 * row against a provider sitting at the ORIGINAL baseline matches neither
 * side, files a bogus conflict, and drops both edits. Replaying the chain in
 * order fixes it: row 1 matches the baseline and writes U1, row 2 then matches
 * U1 and writes U2. A revert (recorded as one more row restoring the provider
 * value) falls out of the same mechanism for free.
 *
 * Pure — no I/O, no client deps, safe for the Cloud Run job bundle.
 */
import type { Amendment, SiteEvidenceDocument } from '../../types/siteEvidence';
import { sedValuesEqual } from './sedValueEquality';

/** An amendment the refreshed evidence overtook. */
export interface InBandAmendmentConflict {
  /** Dotted SED variable path, e.g. `grid.data.substationName`. */
  path: string;
  /** SED section the path belongs to, e.g. `grid`. */
  sectionId: string;
  /** Leaf field within the section, e.g. `data.substationName`. */
  field: string;
  /** The value the operator's last surviving edit set. */
  userValue: unknown;
  /** The value the refreshed evidence now carries. */
  providerValueNow: unknown;
}

export interface ReapplyInBandResult {
  /** Paths whose amended value is in force after the replay. */
  reappliedPaths: string[];
  /** Paths the refreshed evidence overtook — provider value kept. */
  conflicts: InBandAmendmentConflict[];
  /** Paths that no longer resolve on the document (schema drift). */
  orphanedPaths: string[];
}

/** Walk to the parent of a dotted path, cloning each level on the way down. */
function resolveParent(
  root: Record<string, unknown>,
  parts: readonly string[],
): Record<string, unknown> | null {
  let target: Record<string, unknown> = root;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const child = target[parts[i]!];
    if (child == null || typeof child !== 'object' || Array.isArray(child)) return null;
    // Clone on descent so a nested write never mutates an object the caller
    // still holds a reference to (the freshly compiled provider slot is shared
    // by reference after a shallow section spread).
    const cloned = { ...(child as Record<string, unknown>) };
    target[parts[i]!] = cloned;
    target = cloned;
  }
  return target;
}

/**
 * Replay `sed.amendments[]` over `sed` IN PLACE, in recorded order.
 *
 * For each row, the same three-way test the rebuild planner uses:
 *   - current value already equals the correction  → nothing to do (counted)
 *   - current value equals the row's original      → write the correction
 *   - anything else                                → the evidence moved; keep
 *     it and report a conflict rather than silently re-asserting a stale
 *     human decision over a genuinely newer fact.
 *
 * `undefined` and `null` compare equal (see `sedValueEquality`) because the
 * persisted baseline is `null` where the in-memory compile leaves `undefined`.
 *
 * Does NOT append audit rows and does NOT bump `version` — the trail it is
 * replaying is already the document's own, and the caller owns versioning.
 */
export function reapplyInBandAmendments(
  sed: SiteEvidenceDocument,
  options?: { sectionIds?: readonly string[] },
): ReapplyInBandResult {
  const reappliedPaths: string[] = [];
  const conflicts: InBandAmendmentConflict[] = [];
  const orphanedPaths: string[] = [];

  const rows: readonly Amendment[] = Array.isArray(sed?.amendments) ? sed.amendments : [];
  if (rows.length === 0) return { reappliedPaths, conflicts, orphanedPaths };

  const scope = options?.sectionIds ? new Set(options.sectionIds) : null;
  const root = sed as unknown as Record<string, unknown>;

  // Conflicts are keyed per PATH, not per row: a field amended three times
  // produces at most one conflict, describing the last edit that could not be
  // landed — not three rows of noise about the same field.
  const conflictByPath = new Map<string, InBandAmendmentConflict>();
  const reappliedSet = new Set<string>();

  for (const row of rows) {
    const path = row?.field;
    if (typeof path !== 'string' || path.length === 0) continue;
    const parts = path.split('.');
    if (parts.length < 2) continue;
    const sectionId = parts[0]!;
    if (scope && !scope.has(sectionId)) continue;

    const parent = resolveParent(root, parts);
    if (!parent) { orphanedPaths.push(path); continue; }
    const leafKey = parts[parts.length - 1]!;
    // A key that is absent ENTIRELY is schema drift — never vivified here.
    // (A key present with `undefined` is NOT drift; it is an empty value, and
    // the equality helper handles it.)
    if (!(leafKey in parent)) { orphanedPaths.push(path); continue; }

    const current = parent[leafKey];

    if (sedValuesEqual(current, row.correctedValue)) {
      reappliedSet.add(path);
      conflictByPath.delete(path);
      continue;
    }
    if (sedValuesEqual(current, row.originalValue)) {
      parent[leafKey] = row.correctedValue;
      reappliedSet.add(path);
      conflictByPath.delete(path);
      continue;
    }
    // The evidence moved somewhere this chain does not account for. Keep it,
    // and record the divergence so the operator can rule on it. A LATER row
    // for the same path may still resolve cleanly (the chain continues), which
    // is why the conflict is cleared on a successful apply above.
    reappliedSet.delete(path);
    conflictByPath.set(path, {
      path,
      sectionId,
      field: parts.slice(1).join('.'),
      userValue: row.correctedValue,
      providerValueNow: current,
    });
  }

  reappliedPaths.push(...reappliedSet);
  conflicts.push(...conflictByPath.values());
  return { reappliedPaths, conflicts, orphanedPaths };
}
